import { QueryResult, useLazyQuery } from "@apollo/client";
import {
  Product,
  ProductIdByStockNumberResult,
  ProductList,
} from "@towersystems/roam-common/lib/generated-types";
import ProductModel from "../data/model/Product";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  PRODUCTS_QUERY,
  PRODUCT_ID_BY_STOCK_NUMBER_QUERY,
  PRODUCT_QUERY,
} from "./constants";
import { ProductPartialRootScreenContext } from "./context";
import { USER_INPUT_NOT_FOUND_MESSAGE } from "@towersystems/roam-common/lib/constants";
import { useDatabaseProductHelpers } from "../product/hooks";
import { UseAwaitReturn } from "../use-await";
import { makeLoadingState } from "../use-await/utils";

export function useProductIdByStockNumberQuery() {
  const [query] = useLazyQuery<{
    productIdByStockNumber: ProductIdByStockNumberResult;
  }>(PRODUCT_ID_BY_STOCK_NUMBER_QUERY);

  const getProductIdByStockNumber = useCallback(async (stockNumber: string) => {
    const res = await query({
      variables: {
        stockNumber,
      },
    });
    const productId = res.data?.productIdByStockNumber;
    if (!productId) {
      throw new Error(`error.unexpected-failure`);
    }
    return productId;
  }, []);

  return {
    getProductIdByStockNumber,
  };
}

export function useProductQuery(productId: string) {
  const [error, setError] = useState<string | undefined>(undefined);

  const [
    productsQuery,
    {
      data: variantsData,
      loading: variantsLoading,
      error: variantError,
      refetch: variantsRefetch,
    },
  ] = useLazyQuery<{
    products: ProductList;
  }>(PRODUCTS_QUERY);

  const [
    productQuery,
    {
      data: productData,
      loading: productLoading,
      error: productError,
      refetch: productRefetch,
    },
  ] = useLazyQuery<{
    product: Product;
  }>(PRODUCT_QUERY);

  const refetch = useCallback(() => {
    setError(undefined);

    productQuery({
      variables: {
        id: productId,
      },
    }).catch((e) => {
      setError(e.message);
    });

    productsQuery({
      variables: {
        variantProductId: productId,
      },
    }).catch((e) => {
      setError((prev) => prev || e.message);
    });
  }, [productId]);

  useEffect(() => {
    refetch();
  }, [productId]);

  const product = productData?.product;
  const variants: Product[] = variantsData?.products?.items || [];

  return {
    product,
    variants,
    loading: productLoading || variantsLoading,
    error: productError || variantError || error,
    refetch,
  };
}

export const useProductPartialRootScreenContext = () => {
  const context = useContext(ProductPartialRootScreenContext);
  if (context === undefined) {
    throw new Error(
      "useProductPartialRootScreenContext must be used within a ProductPartialRootScreenProvider"
    );
  }
  return context;
};

export const useProductAwaitState = (
  productId?: string
): UseAwaitReturn<ProductModel> | undefined => {
  const [productState, setProductState] = useState<
    UseAwaitReturn<ProductModel>
  >(makeLoadingState(undefined));

  const { findProductById } = useDatabaseProductHelpers();

  useEffect(() => {
    if (!productId) return;
    findProductById(productId)
      .then((res) => {
        const payload = res
          ? ({
              data: res,
              isLoading: false,
              isError: false,
            } as const)
          : ({
              data: undefined,
              isLoading: false,
              isError: true,
              errorMsg: USER_INPUT_NOT_FOUND_MESSAGE,
            } as const);

        setProductState(payload);
      })
      .catch((e) => {
        setProductState({
          data: undefined,
          isLoading: false,
          isError: true,
          errorMsg: "Unexpected error occurred",
        });
      });
  }, [productId]);

  if (!productId) {
    return undefined;
  }

  return productState;
};
